/**
 * 算法配置数据
 * 统一管理所有算法的元数据、代码和可视化配置
 */

export interface AlgorithmCode {
  javascript: string;
  python: string;
  cpp: string;
}

export interface AlgorithmDescription {
  brief: string;           // 简要介绍
  timeComplexity: string;  // 时间复杂度
  spaceComplexity: string; // 空间复杂度
  useCases: string[];      // 应用场景
  pros?: string[];         // 优点
  cons?: string[];         // 缺点
  summary?: string;        // 综合描述
}

export interface AlgorithmConfig {
  id: string;
  name: string;
  category: string;
  modelStyle: string;
  dataType: 'array' | 'linked-list' | 'binary-tree';
  defaultData: number[];
  code: AlgorithmCode | string;  // 支持多语言或单一JS代码
  description?: AlgorithmDescription; // 算法介绍
}

export interface AlgorithmCategory {
  id: string;
  name: string;
  algorithms: string[];  // algorithm ids
}

// 算法分类
export const algorithmCategories: AlgorithmCategory[] = [
  {
    id: 'sorting',
    name: '排序算法',
    algorithms: ['bubble-sort', 'selection-sort', 'insertion-sort', 'quick-sort', 'merge-sort', 'heap-sort', 'shell-sort', 'counting-sort', 'radix-sort']
  },
  {
    id: 'searching',
    name: '搜索算法',
    algorithms: ['linear-search', 'binary-search', 'jump-search']
  },
  {
    id: 'data-structure',
    name: '数据结构',
    algorithms: ['linked-list', 'stack', 'queue', 'binary-tree', 'hash-table', 'union-find']
  },
  {
    id: 'graph',
    name: '图算法',
    algorithms: ['bfs', 'dfs', 'dijkstra', 'prim', 'kruskal', 'topological']
  },
  {
    id: 'dp',
    name: '动态规划',
    algorithms: ['fibonacci', 'knapsack', 'lcs', 'coin-change', 'lis']
  },
  {
    id: 'greedy',
    name: '贪心算法',
    algorithms: ['activity-selection', 'huffman']
  },
  {
    id: 'classic',
    name: '经典算法',
    algorithms: ['hanoi', 'n-queens', 'maze', 'sudoku', 'permutation', 'closest-pair']
  },
  {
    id: 'string',
    name: '字符串',
    algorithms: ['kmp', 'rabin-karp', 'edit-distance']
  },
  {
    id: 'math',
    name: '数学算法',
    algorithms: ['gcd', 'sieve', 'power']
  }
];

// 算法配置映射
export const algorithms: Record<string, AlgorithmConfig> = {
  // ========== 排序算法 ==========
  'bubble-sort': {
    id: 'bubble-sort',
    name: '冒泡排序',
    category: 'sorting',
    modelStyle: 'cylinder',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '冒泡排序是一种简单的排序算法，通过重复遍历待排序序列，比较相邻元素，若顺序错误则交换。每轮遍历后，最大的元素会“冒泡”到末尾。',
      timeComplexity: 'O(n²)',
      spaceComplexity: 'O(1)',
      useCases: ['小规模数据排序', '教学演示', '基本有序的数据'],
      pros: ['实现简单', '空间复杂度低', '稳定排序'],
      cons: ['时间复杂度高', '不适合大数据量'],
      summary: '冒泡排序是最基础的排序算法之一，适合初学者理解排序原理。虽然时间复杂度较高不适合处理大规模数据，但其算法思想简单直观，是学习其他高级排序算法的重要基础。在实际应用中，可以通过提前终止优化来提升性能。'
    },
    code: {
      javascript: `// 冒泡排序
function bubbleSort(arr) {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j] > arr[j + 1]) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
      }
    }
  }
  return arr;
}`,
      python: `# 冒泡排序
def bubble_sort(arr):
    n = len(arr)
    for i in range(n - 1):
        for j in range(n - i - 1):
            if arr[j] > arr[j + 1]:
                arr[j], arr[j + 1] = arr[j + 1], arr[j]
    return arr`,
      cpp: `// 冒泡排序
void bubbleSort(int arr[], int n) {
    for (int i = 0; i < n - 1; i++) {
        for (int j = 0; j < n - i - 1; j++) {
            if (arr[j] > arr[j + 1]) {
                swap(arr[j], arr[j + 1]);
            }
        }
    }
}`
    }
  },
  'selection-sort': {
    id: 'selection-sort',
    name: '选择排序',
    category: 'sorting',
    modelStyle: 'cylinder',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '选择排序每次从未排序部分选择最小（或最大）元素，放到已排序部分的末尾。算法简单直观，交换次数最少。',
      timeComplexity: 'O(n²)',
      spaceComplexity: 'O(1)',
      useCases: ['小规模数据', '内存受限场景', '交换成本高的情况'],
      pros: ['实现简单', '交换次数最少', '空间复杂度低'],
      cons: ['时间复杂度高', '不稳定排序'],
      summary: '选择排序的核心优势在于交换次数最少，适合交换成本较高的场景。虽然时间复杂度与冒泡排序相同，但实际性能稍优。缺点是不稳定，不保持相等元素的原始顺序。'
    },
    code: {
      javascript: `// 选择排序
function selectionSort(arr) {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    let minIdx = i;
    for (let j = i + 1; j < n; j++) {
      if (arr[j] < arr[minIdx]) minIdx = j;
    }
    if (minIdx !== i) {
      [arr[i], arr[minIdx]] = [arr[minIdx], arr[i]];
    }
  }
  return arr;
}`,
      python: `# 选择排序
def selection_sort(arr):
    n = len(arr)
    for i in range(n - 1):
        min_idx = i
        for j in range(i + 1, n):
            if arr[j] < arr[min_idx]:
                min_idx = j
        if min_idx != i:
            arr[i], arr[min_idx] = arr[min_idx], arr[i]
    return arr`,
      cpp: `// 选择排序
void selectionSort(int arr[], int n) {
    for (int i = 0; i < n - 1; i++) {
        int minIdx = i;
        for (int j = i + 1; j < n; j++) {
            if (arr[j] < arr[minIdx]) minIdx = j;
        }
        if (minIdx != i) swap(arr[i], arr[minIdx]);
    }
}`
    }
  },
  'insertion-sort': {
    id: 'insertion-sort',
    name: '插入排序',
    category: 'sorting',
    modelStyle: 'cylinder',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '插入排序类似整理扑克牌，将未排序元素逐个插入到已排序序列的正确位置。对于基本有序的数据非常高效。',
      timeComplexity: 'O(n²)，最优O(n)',
      spaceComplexity: 'O(1)',
      useCases: ['小规模数据', '基本有序的数据', '在线排序'],
      pros: ['简单高效', '稳定排序', '对有序数据效率高'],
      cons: ['平均时间复杂度高', '不适合大数据量'],
      summary: '插入排序是少数在最优情况下达到O(n)的排序算法，对基本有序的数据非常高效。实际应用中常作为快速排序等高级算法的小规模数据处理子程序。'
    },
    code: {
      javascript: `// 插入排序
function insertionSort(arr) {
  for (let i = 1; i < arr.length; i++) {
    let key = arr[i], j = i - 1;
    while (j >= 0 && arr[j] > key) {
      arr[j + 1] = arr[j];
      j--;
    }
    arr[j + 1] = key;
  }
  return arr;
}`,
      python: `# 插入排序
def insertion_sort(arr):
    for i in range(1, len(arr)):
        key = arr[i]
        j = i - 1
        while j >= 0 and arr[j] > key:
            arr[j + 1] = arr[j]
            j -= 1
        arr[j + 1] = key
    return arr`,
      cpp: `// 插入排序
void insertionSort(int arr[], int n) {
    for (int i = 1; i < n; i++) {
        int key = arr[i], j = i - 1;
        while (j >= 0 && arr[j] > key) {
            arr[j + 1] = arr[j];
            j--;
        }
        arr[j + 1] = key;
    }
}`
    }
  },
  'quick-sort': {
    id: 'quick-sort',
    name: '快速排序',
    category: 'sorting',
    modelStyle: 'hexagon',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '快速排序采用分治思想，选择一个基准元素，将数组分为两部分，小于基准的在左，大于基准的在右，递归排序。',
      timeComplexity: 'O(n log n)，最坏O(n²)',
      spaceComplexity: 'O(log n)',
      useCases: ['通用排序', '大规模数据', '内部排序'],
      pros: ['平均性能优秀', '原地排序', '缓存友好'],
      cons: ['最坏情况性能差', '不稳定排序', '递归栈开销'],
      summary: '快速排序是实际应用中最广泛使用的排序算法，平均性能优秀。分治思想的经典体现，但需注意在已排序数据上选择合适的基准元素避免退化。各类编程语言标准库的首选排序算法。'
    },
    code: {
      javascript: `// 快速排序
function quickSort(arr, left = 0, right = arr.length - 1) {
  if (left < right) {
    const pivotIdx = partition(arr, left, right);
    quickSort(arr, left, pivotIdx - 1);
    quickSort(arr, pivotIdx + 1, right);
  }
  return arr;
}

function partition(arr, left, right) {
  const pivot = arr[right];
  let i = left - 1;
  for (let j = left; j < right; j++) {
    if (arr[j] <= pivot) {
      i++;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
  [arr[i + 1], arr[right]] = [arr[right], arr[i + 1]];
  return i + 1;
}`,
      python: `# 快速排序
def quick_sort(arr, left=0, right=None):
    if right is None:
        right = len(arr) - 1
    if left < right:
        pivot_idx = partition(arr, left, right)
        quick_sort(arr, left, pivot_idx - 1)
        quick_sort(arr, pivot_idx + 1, right)
    return arr

def partition(arr, left, right):
    pivot = arr[right]
    i = left - 1
    for j in range(left, right):
        if arr[j] <= pivot:
            i += 1
            arr[i], arr[j] = arr[j], arr[i]
    arr[i + 1], arr[right] = arr[right], arr[i + 1]
    return i + 1`,
      cpp: `// 快速排序
int partition(int arr[], int left, int right) {
    int pivot = arr[right];
    int i = left - 1;
    for (int j = left; j < right; j++) {
        if (arr[j] <= pivot) {
            i++;
            swap(arr[i], arr[j]);
        }
    }
    swap(arr[i + 1], arr[right]);
    return i + 1;
}

void quickSort(int arr[], int left, int right) {
    if (left < right) {
        int pivotIdx = partition(arr, left, right);
        quickSort(arr, left, pivotIdx - 1);
        quickSort(arr, pivotIdx + 1, right);
    }
}`
    }
  },
  'merge-sort': {
    id: 'merge-sort',
    name: '归并排序',
    category: 'sorting',
    modelStyle: 'hexagon',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '归并排序使用分治策略，将数组不断对半分割直到单个元素，然后合并为有序序列。稳定且性能稳定。',
      timeComplexity: 'O(n log n)',
      spaceComplexity: 'O(n)',
      useCases: ['外部排序', '链表排序', '需要稳定性的场景'],
      pros: ['时间复杂度稳定', '稳定排序', '适合链表'],
      cons: ['需要额外空间', '小数据不如插入排序'],
      summary: '归并排序是唯一保证O(n log n)稳定时间复杂度的比较排序算法，且保持稳定性。分治策略的经典案例，常用于外部排序和链表排序。代价是需要O(n)额外空间。'
    },
    code: {
      javascript: `// 归并排序
function mergeSort(arr) {
  if (arr.length <= 1) return arr;
  const mid = Math.floor(arr.length / 2);
  const left = mergeSort(arr.slice(0, mid));
  const right = mergeSort(arr.slice(mid));
  return merge(left, right);
}

function merge(left, right) {
  const result = [];
  let i = 0, j = 0;
  while (i < left.length && j < right.length) {
    result.push(left[i] <= right[j] ? left[i++] : right[j++]);
  }
  return result.concat(left.slice(i)).concat(right.slice(j));
}`,
      python: `# 归并排序
def merge_sort(arr):
    if len(arr) <= 1:
        return arr
    mid = len(arr) // 2
    left = merge_sort(arr[:mid])
    right = merge_sort(arr[mid:])
    return merge(left, right)

def merge(left, right):
    result = []
    i = j = 0
    while i < len(left) and j < len(right):
        if left[i] <= right[j]:
            result.append(left[i])
            i += 1
        else:
            result.append(right[j])
            j += 1
    return result + left[i:] + right[j:]`,
      cpp: `// 归并排序
void merge(int arr[], int l, int m, int r) {
    int n1 = m - l + 1, n2 = r - m;
    int L[n1], R[n2];
    for (int i = 0; i < n1; i++) L[i] = arr[l + i];
    for (int j = 0; j < n2; j++) R[j] = arr[m + 1 + j];
    int i = 0, j = 0, k = l;
    while (i < n1 && j < n2)
        arr[k++] = (L[i] <= R[j]) ? L[i++] : R[j++];
    while (i < n1) arr[k++] = L[i++];
    while (j < n2) arr[k++] = R[j++];
}

void mergeSort(int arr[], int l, int r) {
    if (l < r) {
        int m = l + (r - l) / 2;
        mergeSort(arr, l, m);
        mergeSort(arr, m + 1, r);
        merge(arr, l, m, r);
    }
}`
    }
  },
  'heap-sort': {
    id: 'heap-sort',
    name: '堆排序',
    category: 'sorting',
    modelStyle: 'pyramid',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '堆排序利用堆这种数据结构进行排序。先构建最大堆，然后逐个取出堆顶元素，得到有序序列。',
      timeComplexity: 'O(n log n)',
      spaceComplexity: 'O(1)',
      useCases: ['优先队列', 'Top K问题', '大数据量排序'],
      pros: ['时间复杂度稳定', '原地排序', '适合大数据'],
      cons: ['不稳定排序', '缓存不友好', '常数因子较大'],
      summary: '堆排序结合了堆数据结构的特性，空间复杂度O(1)且时间稳定。特别适合求最大/最小K个元素的场景。优先队列的底层实现，是理解树结构的重要算法。'
    },
    code: {
      javascript: `// 堆排序
function heapSort(arr) {
  const n = arr.length;
  // 构建最大堆
  for (let i = Math.floor(n / 2) - 1; i >= 0; i--) {
    heapify(arr, n, i);
  }
  // 逐个提取元素
  for (let i = n - 1; i > 0; i--) {
    [arr[0], arr[i]] = [arr[i], arr[0]];
    heapify(arr, i, 0);
  }
  return arr;
}

function heapify(arr, n, i) {
  let largest = i;
  const left = 2 * i + 1, right = 2 * i + 2;
  if (left < n && arr[left] > arr[largest]) largest = left;
  if (right < n && arr[right] > arr[largest]) largest = right;
  if (largest !== i) {
    [arr[i], arr[largest]] = [arr[largest], arr[i]];
    heapify(arr, n, largest);
  }
}`,
      python: `# 堆排序
def heap_sort(arr):
    n = len(arr)
    # 构建最大堆
    for i in range(n // 2 - 1, -1, -1):
        heapify(arr, n, i)
    # 逐个提取元素
    for i in range(n - 1, 0, -1):
        arr[0], arr[i] = arr[i], arr[0]
        heapify(arr, i, 0)
    return arr

def heapify(arr, n, i):
    largest = i
    left, right = 2 * i + 1, 2 * i + 2
    if left < n and arr[left] > arr[largest]:
        largest = left
    if right < n and arr[right] > arr[largest]:
        largest = right
    if largest != i:
        arr[i], arr[largest] = arr[largest], arr[i]
        heapify(arr, n, largest)`,
      cpp: `// 堆排序
void heapify(int arr[], int n, int i) {
    int largest = i;
    int left = 2 * i + 1, right = 2 * i + 2;
    if (left < n && arr[left] > arr[largest]) largest = left;
    if (right < n && arr[right] > arr[largest]) largest = right;
    if (largest != i) {
        swap(arr[i], arr[largest]);
        heapify(arr, n, largest);
    }
}

void heapSort(int arr[], int n) {
    // 构建最大堆
    for (int i = n / 2 - 1; i >= 0; i--)
        heapify(arr, n, i);
    // 逐个提取元素
    for (int i = n - 1; i > 0; i--) {
        swap(arr[0], arr[i]);
        heapify(arr, i, 0);
    }
}`
    }
  },
  'shell-sort': {
    id: 'shell-sort',
    name: '希尔排序',
    category: 'sorting',
    modelStyle: 'cylinder',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '希尔排序是插入排序的改进版，通过设置间隔序列将数组分组，对每组进行插入排序，逐步缩小间隔直至为1。',
      timeComplexity: 'O(n log n) ~ O(n²)',
      spaceComplexity: 'O(1)',
      useCases: ['中等规模数据排序', '对插入排序的优化场景'],
      pros: ['比插入排序快', '原地排序', '实现较简单'],
      cons: ['时间复杂度与间隔序列相关', '不稳定排序'],
      summary: '希尔排序是第一个突破O(n²)的排序算法，通过间隔分组改进插入排序。实际性能优秀，常数因子小，在中等规模数据上表现超越很多高级排序算法。'
    },
    code: {
      javascript: `// 希尔排序
function shellSort(arr) {
  const n = arr.length;
  for (let gap = Math.floor(n / 2); gap > 0; gap = Math.floor(gap / 2)) {
    for (let i = gap; i < n; i++) {
      const temp = arr[i];
      let j = i;
      while (j >= gap && arr[j - gap] > temp) {
        arr[j] = arr[j - gap];
        j -= gap;
      }
      arr[j] = temp;
    }
  }
  return arr;
}`,
      python: `# 希尔排序
def shell_sort(arr):
    n = len(arr)
    gap = n // 2
    while gap > 0:
        for i in range(gap, n):
            temp = arr[i]
            j = i
            while j >= gap and arr[j - gap] > temp:
                arr[j] = arr[j - gap]
                j -= gap
            arr[j] = temp
        gap //= 2
    return arr`,
      cpp: `// 希尔排序
void shellSort(int arr[], int n) {
    for (int gap = n / 2; gap > 0; gap /= 2) {
        for (int i = gap; i < n; i++) {
            int temp = arr[i];
            int j = i;
            while (j >= gap && arr[j - gap] > temp) {
                arr[j] = arr[j - gap];
                j -= gap;
            }
            arr[j] = temp;
        }
    }
}`
    }
  },
  'counting-sort': {
    id: 'counting-sort',
    name: '计数排序',
    category: 'sorting',
    modelStyle: 'dataBlock',
    dataType: 'array',
    defaultData: [4, 2, 2, 8, 3, 3, 1],
    description: {
      brief: '计数排序是一种非比较排序算法，通过统计每个元素出现次数来排序。适用于整数且范围较小的数据。',
      timeComplexity: 'O(n + k)',
      spaceComplexity: 'O(k)',
      useCases: ['整数排序', '数据范围有限的场景', '成绩统计'],
      pros: ['线性时间复杂度', '稳定排序', '非比较排序'],
      cons: ['只能排序整数', '空间与数据范围相关'],
      summary: '计数排序是线性时间排序的代表，通过空间换时间实现突破。适合整数且范围有限的场景，如成绩统计、年龄排序等。是基数排序的基础。'
    },
    code: {
      javascript: `// 计数排序
function countingSort(arr) {
  const max = Math.max(...arr);
  const count = new Array(max + 1).fill(0);
  for (const num of arr) count[num]++;
  const result = [];
  for (let i = 0; i <= max; i++) {
    while (count[i] > 0) {
      result.push(i);
      count[i]--;
    }
  }
  return result;
}`,
      python: `# 计数排序
def counting_sort(arr):
    max_val = max(arr)
    count = [0] * (max_val + 1)
    for num in arr:
        count[num] += 1
    result = []
    for i in range(max_val + 1):
        while count[i] > 0:
            result.append(i)
            count[i] -= 1
    return result`,
      cpp: `// 计数排序
void countingSort(int arr[], int n) {
    int max_val = *max_element(arr, arr + n);
    int count[max_val + 1] = {0};
    for (int i = 0; i < n; i++) count[arr[i]]++;
    int idx = 0;
    for (int i = 0; i <= max_val; i++) {
        while (count[i] > 0) {
            arr[idx++] = i;
            count[i]--;
        }
    }
}`
    }
  },
  'radix-sort': {
    id: 'radix-sort',
    name: '基数排序',
    category: 'sorting',
    modelStyle: 'hexagon',
    dataType: 'array',
    defaultData: [170, 45, 75, 90, 802, 24, 2, 66],
    description: {
      brief: '基数排序按位数从低到高（或从高到低）依次排序，每一位使用稳定的计数排序。适合处理整数或字符串。',
      timeComplexity: 'O(d × (n + k))',
      spaceComplexity: 'O(n + k)',
      useCases: ['大量整数排序', '字符串排序', '电话号码排序'],
      pros: ['线性时间复杂度', '稳定排序', '适合大量数据'],
      cons: ['只适合特定数据类型', '需要额外空间'],
      summary: '基数排序是处理大量整数或字符串的绝佳选择，线性时间且稳定。电话号码排序、身份证排序等场景的首选。理解它需要掌握计数排序作为子程序。'
    },
    code: {
      javascript: `// 基数排序
function radixSort(arr) {
  const max = Math.max(...arr);
  for (let exp = 1; Math.floor(max / exp) > 0; exp *= 10) {
    countingSortByDigit(arr, exp);
  }
  return arr;
}

function countingSortByDigit(arr, exp) {
  const n = arr.length;
  const output = new Array(n);
  const count = new Array(10).fill(0);
  for (let i = 0; i < n; i++) {
    count[Math.floor(arr[i] / exp) % 10]++;
  }
  for (let i = 1; i < 10; i++) count[i] += count[i - 1];
  for (let i = n - 1; i >= 0; i--) {
    const digit = Math.floor(arr[i] / exp) % 10;
    output[--count[digit]] = arr[i];
  }
  for (let i = 0; i < n; i++) arr[i] = output[i];
}`,
      python: `# 基数排序
def radix_sort(arr):
    max_val = max(arr)
    exp = 1
    while max_val // exp > 0:
        counting_sort_by_digit(arr, exp)
        exp *= 10
    return arr

def counting_sort_by_digit(arr, exp):
    n = len(arr)
    output = [0] * n
    count = [0] * 10
    for i in range(n):
        count[(arr[i] // exp) % 10] += 1
    for i in range(1, 10):
        count[i] += count[i - 1]
    for i in range(n - 1, -1, -1):
        digit = (arr[i] // exp) % 10
        count[digit] -= 1
        output[count[digit]] = arr[i]
    for i in range(n):
        arr[i] = output[i]`,
      cpp: `// 基数排序
void countingSortByDigit(int arr[], int n, int exp) {
    int output[n], count[10] = {0};
    for (int i = 0; i < n; i++)
        count[(arr[i] / exp) % 10]++;
    for (int i = 1; i < 10; i++)
        count[i] += count[i - 1];
    for (int i = n - 1; i >= 0; i--) {
        int digit = (arr[i] / exp) % 10;
        output[--count[digit]] = arr[i];
    }
    for (int i = 0; i < n; i++) arr[i] = output[i];
}

void radixSort(int arr[], int n) {
    int max_val = *max_element(arr, arr + n);
    for (int exp = 1; max_val / exp > 0; exp *= 10)
        countingSortByDigit(arr, n, exp);
}`
    }
  },

  // ========== 搜索算法 ==========
  'linear-search': {
    id: 'linear-search',
    name: '线性查找',
    category: 'searching',
    modelStyle: 'cube',
    dataType: 'array',
    defaultData: [5, 3, 8, 1, 9, 2, 7, 4, 6],
    description: {
      brief: '线性查找是最简单的查找算法，从头到尾逐个检查每个元素，直到找到目标或遍历完数组。',
      timeComplexity: 'O(n)',
      spaceComplexity: 'O(1)',
      useCases: ['无序数据查找', '小规模数据', '链表查找'],
      pros: ['实现简单', '无需有序', '适合链表'],
      cons: ['效率低', '不适合大数据量'],
      summary: '线性查找是最基础的查找方法，无任何前置要求。虽然效率较低，但在无序数据或小规模数据中仍然实用。是理解查找算法的入门基础，也是其他高级查找算法的对比基准。'
    },
    code: {
      javascript: `// 线性查找
function linearSearch(arr, target) {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === target) return i;
  }
  return -1;
}`,
      python: `# 线性查找
def linear_search(arr, target):
    for i in range(len(arr)):
        if arr[i] == target:
            return i
    return -1`,
      cpp: `// 线性查找
int linearSearch(int arr[], int n, int target) {
    for (int i = 0; i < n; i++) {
        if (arr[i] == target) return i;
    }
    return -1;
}`
    }
  },
  'binary-search': {
    id: 'binary-search',
    name: '二分查找',
    category: 'searching',
    modelStyle: 'cube',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    description: {
      brief: '二分查找是一种高效的查找算法，每次将搜索范围缩小一半。要求数据已排序，是最常用的查找算法之一。',
      timeComplexity: 'O(log n)',
      spaceComplexity: 'O(1)',
      useCases: ['有序数组查找', '数据库索引', '查找边界值'],
      pros: ['时间复杂度低', '效率极高', '实现简单'],
      cons: ['需要数据有序', '不适合链表', '插入删除代价高'],
      summary: '二分查找是有序数据查找的首选算法，效率极高。是分治思想的典型应用，面试必考算法。注意边界条件处理，避免死循环和越界。'
    },
    code: {
      javascript: `// 二分查找
function binarySearch(arr, target) {
  let left = 0, right = arr.length - 1;
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    if (arr[mid] === target) return mid;
    else if (arr[mid] < target) left = mid + 1;
    else right = mid - 1;
  }
  return -1;
}`,
      python: `# 二分查找
def binary_search(arr, target):
    left, right = 0, len(arr) - 1
    while left <= right:
        mid = (left + right) // 2
        if arr[mid] == target:
            return mid
        elif arr[mid] < target:
            left = mid + 1
        else:
            right = mid - 1
    return -1`,
      cpp: `// 二分查找
int binarySearch(int arr[], int n, int target) {
    int left = 0, right = n - 1;
    while (left <= right) {
        int mid = left + (right - left) / 2;
        if (arr[mid] == target) return mid;
        else if (arr[mid] < target) left = mid + 1;
        else right = mid - 1;
    }
    return -1;
}`
    }
  },
  'jump-search': {
    id: 'jump-search',
    name: '跳跃查找',
    category: 'searching',
    modelStyle: 'cube',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    description: {
      brief: '跳跃查找在有序数组中按固定步长跳跃，找到区间后线性搜索。步长通常为√n，是线性查找和二分查找的折中。',
      timeComplexity: 'O(√n)',
      spaceComplexity: 'O(1)',
      useCases: ['有序数组查找', '不适合二分的场景', '回跳代价高的存储'],
      pros: ['比线性查找快', '实现简单', '适合特定场景'],
      cons: ['需要有序数据', '比二分查找慢'],
      summary: '跳跃查找是线性查找和二分查找的折中方案。在某些特殊存储介质（如磁带）上，回跳成本高时比二分查找更优。是理解查找算法设计权衡的好例子。'
    },
    code: {
      javascript: `// 跳跃查找
function jumpSearch(arr, target) {
  const n = arr.length;
  let step = Math.floor(Math.sqrt(n));
  let prev = 0;
  while (arr[Math.min(step, n) - 1] < target) {
    prev = step;
    step += Math.floor(Math.sqrt(n));
    if (prev >= n) return -1;
  }
  while (arr[prev] < target) {
    prev++;
    if (prev === Math.min(step, n)) return -1;
  }
  return arr[prev] === target ? prev : -1;
}`,
      python: `# 跳跃查找
import math

def jump_search(arr, target):
    n = len(arr)
    step = int(math.sqrt(n))
    prev = 0
    while arr[min(step, n) - 1] < target:
        prev = step
        step += int(math.sqrt(n))
        if prev >= n:
            return -1
    while arr[prev] < target:
        prev += 1
        if prev == min(step, n):
            return -1
    return prev if arr[prev] == target else -1`,
      cpp: `// 跳跃查找
#include <cmath>

int jumpSearch(int arr[], int n, int target) {
    int step = sqrt(n);
    int prev = 0;
    while (arr[min(step, n) - 1] < target) {
        prev = step;
        step += sqrt(n);
        if (prev >= n) return -1;
    }
    while (arr[prev] < target) {
        prev++;
        if (prev == min(step, n)) return -1;
    }
    return arr[prev] == target ? prev : -1;
}`
    }
  },

  // ========== 数据结构 ==========
  'linked-list': {
    id: 'linked-list',
    name: '链表操作',
    category: 'data-structure',
    modelStyle: 'node',
    dataType: 'linked-list',
    defaultData: [1, 2, 3, 4, 5],
    description: {
      brief: '链表是一种线性数据结构，每个节点包含数据和指向下一个节点的指针。支持动态大小和高效插入删除。',
      timeComplexity: '插入/删除O(1)，查找O(n)',
      spaceComplexity: 'O(n)',
      useCases: ['动态数据存储', '实现栈和队列', '撤销操作'],
      pros: ['动态大小', '插入删除快', '无需连续内存'],
      cons: ['随机访问慢', '需要额外指针空间', '缓存不友好'],
      summary: '链表是动态数据结构的基础，适合频繁插入删除的场景。是栈、队列、哈希表等高级数据结构的底层实现。理解指针操作是掌握链表的关键。'
    },
    code: {
      javascript: `// 链表节点
class ListNode {
  constructor(val) {
    this.val = val;
    this.next = null;
  }
}

// 反转链表
function reverse(head) {
  let prev = null, curr = head;
  while (curr) {
    const next = curr.next;
    curr.next = prev;
    prev = curr;
    curr = next;
  }
  return prev;
}`,
      python: `# 链表节点
class ListNode:
    def __init__(self, val=0):
        self.val = val
        self.next = None

# 反转链表
def reverse(head):
    prev, curr = None, head
    while curr:
        next_node = curr.next
        curr.next = prev
        prev = curr
        curr = next_node
    return prev`,
      cpp: `// 链表节点
struct ListNode {
    int val;
    ListNode* next;
    ListNode(int x) : val(x), next(nullptr) {}
};

// 反转链表
ListNode* reverse(ListNode* head) {
    ListNode *prev = nullptr, *curr = head;
    while (curr) {
        ListNode* next = curr->next;
        curr->next = prev;
        prev = curr;
        curr = next;
    }
    return prev;
}`
    }
  },
  'stack': {
    id: 'stack',
    name: '栈操作',
    category: 'data-structure',
    modelStyle: 'dataBlock',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5],
    description: {
      brief: '栈是后进先出(LIFO)的数据结构，只允许在栈顶进行插入和删除。广泛用于函数调用、表达式求值等场景。',
      timeComplexity: 'push/pop O(1)',
      spaceComplexity: 'O(n)',
      useCases: ['函数调用栈', '撤销功能', '括号匹配', '表达式求值'],
      pros: ['操作简单高效', '内存管理方便', 'LIFO特性'],
      cons: ['只能访问栈顶', '不支持随机访问'],
      summary: '栈是计算机科学中最重要的数据结构之一。函数调用、括号匹配、表达式求值、浏览器前进后退都依赖栈。LIFO特性使其成为回溯类问题的首选。'
    },
    code: {
      javascript: `// 栈 - 后进先出 (LIFO)
class Stack {
  constructor() { this.items = []; }
  push(el) { this.items.push(el); }
  pop() { return this.items.pop(); }
  peek() { return this.items[this.items.length - 1]; }
  isEmpty() { return this.items.length === 0; }
  size() { return this.items.length; }
}`,
      python: `# 栈 - 后进先出 (LIFO)
class Stack:
    def __init__(self):
        self.items = []
    
    def push(self, item):
        self.items.append(item)
    
    def pop(self):
        return self.items.pop() if self.items else None
    
    def peek(self):
        return self.items[-1] if self.items else None
    
    def is_empty(self):
        return len(self.items) == 0`,
      cpp: `// 栈 - 后进先出 (LIFO)
#include <stack>
using namespace std;

stack<int> s;
s.push(1);      // 入栈
s.pop();        // 出栈
int top = s.top();  // 查看栈顶
bool empty = s.empty();  // 是否为空`
    }
  },
  'queue': {
    id: 'queue',
    name: '队列操作',
    category: 'data-structure',
    modelStyle: 'dataBlock',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5],
    description: {
      brief: '队列是先进先出(FIFO)的数据结构，元素从队尾加入，从队头移除。广泛用于任务调度、BFS等。',
      timeComplexity: 'enqueue/dequeue O(1)',
      spaceComplexity: 'O(n)',
      useCases: ['BFS广度优先搜索', '任务队列', '打印机队列', '消息队列'],
      pros: ['FIFO特性', '操作简单', '广泛应用'],
      cons: ['不支持随机访问', '只能访问队头队尾'],
      summary: '队列是BFS、任务调度、消息系统的核心数据结构。FIFO特性保证了公平性和顺序性。变体如优先队列、双端队列扩展了应用场景。'
    },
    code: {
      javascript: `// 队列 - 先进先出 (FIFO)
class Queue {
  constructor() { this.items = []; }
  enqueue(el) { this.items.push(el); }
  dequeue() { return this.items.shift(); }
  front() { return this.items[0]; }
  isEmpty() { return this.items.length === 0; }
  size() { return this.items.length; }
}`,
      python: `# 队列 - 先进先出 (FIFO)
from collections import deque

class Queue:
    def __init__(self):
        self.items = deque()
    
    def enqueue(self, item):
        self.items.append(item)
    
    def dequeue(self):
        return self.items.popleft() if self.items else None
    
    def front(self):
        return self.items[0] if self.items else None
    
    def is_empty(self):
        return len(self.items) == 0`,
      cpp: `// 队列 - 先进先出 (FIFO)
#include <queue>
using namespace std;

queue<int> q;
q.push(1);      // 入队
q.pop();        // 出队
int front = q.front();  // 查看队头
bool empty = q.empty();  // 是否为空`
    }
  },
  'binary-tree': {
    id: 'binary-tree',
    name: '二叉树遍历',
    category: 'data-structure',
    modelStyle: 'node',
    dataType: 'binary-tree',
    defaultData: [5, 3, 7, 1, 4, 6, 8],
    description: {
      brief: '二叉树是每个节点最多有两个子节点的树结构。遍历方式包括前序、中序、后序和层序遍历。',
      timeComplexity: '遍历O(n)，查找O(log n)~O(n)',
      spaceComplexity: 'O(n)',
      useCases: ['表达式求值', '文件系统', '数据库索引', 'Huffman编码'],
      pros: ['层次结构清晰', '查找效率高', '适合递归'],
      cons: ['可能退化为链表', '需要平衡优化'],
      summary: '二叉树是层次数据结构的基础，搜索树、堆、Huffman树都是其变体。前中后序遍历是递归思想的经典体现。平衡优化（AVL、红黑树）解决退化问题。'
    },
    code: {
      javascript: `// 二叉树节点
class TreeNode {
  constructor(val) {
    this.val = val;
    this.left = this.right = null;
  }
}

// 前序遍历
function preorder(root) {
  if (!root) return;
  console.log(root.val);
  preorder(root.left);
  preorder(root.right);
}

// 中序遍历
function inorder(root) {
  if (!root) return;
  inorder(root.left);
  console.log(root.val);
  inorder(root.right);
}`,
      python: `# 二叉树节点
class TreeNode:
    def __init__(self, val=0):
        self.val = val
        self.left = self.right = None

# 前序遍历
def preorder(root):
    if not root:
        return
    print(root.val)
    preorder(root.left)
    preorder(root.right)

# 中序遍历
def inorder(root):
    if not root:
        return
    inorder(root.left)
    print(root.val)
    inorder(root.right)`,
      cpp: `// 二叉树节点
struct TreeNode {
    int val;
    TreeNode *left, *right;
    TreeNode(int x) : val(x), left(nullptr), right(nullptr) {}
};

// 前序遍历
void preorder(TreeNode* root) {
    if (!root) return;
    cout << root->val << endl;
    preorder(root->left);
    preorder(root->right);
}

// 中序遍历
void inorder(TreeNode* root) {
    if (!root) return;
    inorder(root->left);
    cout << root->val << endl;
    inorder(root->right);
}`
    }
  },
  'hash-table': {
    id: 'hash-table',
    name: '哈希表',
    category: 'data-structure',
    modelStyle: 'dataBlock',
    dataType: 'array',
    defaultData: [3, 7, 1, 9, 5],
    description: {
      brief: '哈希表通过哈希函数将键映射到数组位置，实现平均O(1)的查找、插入和删除操作。',
      timeComplexity: '平均O(1)，最坏O(n)',
      spaceComplexity: 'O(n)',
      useCases: ['快速查找', '缓存实现', '去重', '计数'],
      pros: ['查找极快', '插入删除快', '通用性强'],
      cons: ['可能有哈希冲突', '无序', '空间开销'],
      summary: '哈希表是空间换时间的典范，O(1)查找使其成为最常用的数据结构之一。理解哈希函数设计和冲突解决是关键。各语言Map/Dict的底层实现。'
    },
    code: {
      javascript: `// 哈希表
class HashTable {
  constructor(size = 53) {
    this.keyMap = new Array(size);
  }
  
  _hash(key) {
    let total = 0, PRIME = 31;
    for (let i = 0; i < Math.min(key.length, 100); i++) {
      total = (total * PRIME + key.charCodeAt(i) - 96) % this.keyMap.length;
    }
    return total;
  }
  
  set(key, val) {
    const idx = this._hash(key);
    if (!this.keyMap[idx]) this.keyMap[idx] = [];
    this.keyMap[idx].push([key, val]);
  }
  
  get(key) {
    const idx = this._hash(key);
    if (this.keyMap[idx]) {
      for (const [k, v] of this.keyMap[idx]) {
        if (k === key) return v;
      }
    }
  }
}`,
      python: `# 哈希表
class HashTable:
    def __init__(self, size=53):
        self.size = size
        self.table = [[] for _ in range(size)]
    
    def _hash(self, key):
        total = 0
        for char in key[:100]:
            total = (total * 31 + ord(char)) % self.size
        return total
    
    def set(self, key, value):
        idx = self._hash(key)
        for pair in self.table[idx]:
            if pair[0] == key:
                pair[1] = value
                return
        self.table[idx].append([key, value])
    
    def get(self, key):
        idx = self._hash(key)
        for k, v in self.table[idx]:
            if k == key:
                return v
        return None`,
      cpp: `// 哈希表 (使用STL)
#include <unordered_map>
using namespace std;

unordered_map<string, int> hashTable;

// 插入
hashTable["key"] = 100;

// 查找
if (hashTable.find("key") != hashTable.end()) {
    int val = hashTable["key"];
}

// 删除
hashTable.erase("key");`
    }
  },

  // ========== 图算法 ==========
  'bfs': {
    id: 'bfs',
    name: '广度优先搜索',
    category: 'graph',
    modelStyle: 'node',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6, 7],
    description: {
      brief: '广度优先搜索(BFS)从起始节点开始，逐层访问所有邻居节点。用于找最短路径、层序遍历等。',
      timeComplexity: 'O(V + E)',
      spaceComplexity: 'O(V)',
      useCases: ['最短路径', '层序遍历', '连通性检测', '社交网络分析'],
      pros: ['找最短路径', '层序遍历', '适合无权图'],
      cons: ['空间开销大', '不适合深度搜索'],
      summary: 'BFS是图遍历的核心算法，在无权图中保证找到最短路径。队列是其实现基础。社交网络的六度分隔、地图的最短路线都依赖BFS。'
    },
    code: {
      javascript: `// 广度优先搜索 (BFS)
function bfs(graph, start) {
  const visited = new Set();
  const queue = [start];
  const result = [];
  while (queue.length > 0) {
    const node = queue.shift();
    if (!visited.has(node)) {
      visited.add(node);
      result.push(node);
      for (const neighbor of graph[node]) {
        queue.push(neighbor);
      }
    }
  }
  return result;
}`,
      python: `# 广度优先搜索 (BFS)
from collections import deque

def bfs(graph, start):
    visited = set()
    queue = deque([start])
    result = []
    while queue:
        node = queue.popleft()
        if node not in visited:
            visited.add(node)
            result.append(node)
            for neighbor in graph[node]:
                queue.append(neighbor)
    return result`,
      cpp: `// 广度优先搜索 (BFS)
#include <queue>
#include <vector>
#include <set>

vector<int> bfs(vector<vector<int>>& graph, int start) {
    set<int> visited;
    queue<int> q;
    vector<int> result;
    q.push(start);
    while (!q.empty()) {
        int node = q.front(); q.pop();
        if (visited.find(node) == visited.end()) {
            visited.insert(node);
            result.push_back(node);
            for (int neighbor : graph[node])
                q.push(neighbor);
        }
    }
    return result;
}`
    }
  },
  'dfs': {
    id: 'dfs',
    name: '深度优先搜索',
    category: 'graph',
    modelStyle: 'node',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6, 7],
    description: {
      brief: '深度优先搜索(DFS)沿着一条路径走到底，然后回溯探索其他分支。适合路径存在性、拓扑排序等。',
      timeComplexity: 'O(V + E)',
      spaceComplexity: 'O(V)',
      useCases: ['路径查找', '拓扑排序', '连通分量', '迷宫求解'],
      pros: ['内存效率高', '容易实现', '适合深度探索'],
      cons: ['可能无限循环', '不保证最短路径'],
      summary: 'DFS是图遍历和回溯算法的基础。递归实现简洁优雅，栈实现节省空间。连通分量、拓扑排序、迷宫求解都是DFS的应用。'
    },
    code: {
      javascript: `// 深度优先搜索 (DFS)
function dfs(graph, start, visited = new Set()) {
  visited.add(start);
  console.log(start);
  for (const neighbor of graph[start]) {
    if (!visited.has(neighbor)) {
      dfs(graph, neighbor, visited);
    }
  }
  return visited;
}`,
      python: `# 深度优先搜索 (DFS)
def dfs(graph, start, visited=None):
    if visited is None:
        visited = set()
    visited.add(start)
    print(start)
    for neighbor in graph[start]:
        if neighbor not in visited:
            dfs(graph, neighbor, visited)
    return visited`,
      cpp: `// 深度优先搜索 (DFS)
#include <vector>
#include <set>

void dfs(vector<vector<int>>& graph, int node, set<int>& visited) {
    visited.insert(node);
    cout << node << endl;
    for (int neighbor : graph[node]) {
        if (visited.find(neighbor) == visited.end()) {
            dfs(graph, neighbor, visited);
        }
    }
}`
    }
  },
  'dijkstra': {
    id: 'dijkstra',
    name: '最短路径',
    category: 'graph',
    modelStyle: 'helix',  // 螺旋体 - 寻路算法
    dataType: 'array',
    defaultData: [0, 1, 2, 3, 4, 5],
    description: {
      brief: 'Dijkstra算法用于计算加权图中从起点到所有其他顶点的最短路径。使用贪心策略，适用于非负权图。',
      timeComplexity: 'O((V+E) log V)',
      spaceComplexity: 'O(V)',
      useCases: ['地图导航', '网络路由', '游戏寻路', '行程规划'],
      pros: ['高效求最短路径', '贪心策略', '广泛应用'],
      cons: ['不支持负权', '需要优先队列'],
      summary: 'Dijkstra是最短路径问题的经典解法，贪心策略保证最优。地图导航、网络路由的核心算法。Bellman-Ford算法可处理负权边。'
    },
    code: `// Dijkstra 最短路径算法
function dijkstra(graph, start) {
  const distances = {};
  const visited = new Set();
  const pq = new PriorityQueue();
  
  // 初始化距离
  for (const node in graph) {
    distances[node] = node === start ? 0 : Infinity;
  }
  
  pq.enqueue(start, 0);
  
  while (!pq.isEmpty()) {
    const { node } = pq.dequeue();
    if (visited.has(node)) continue;
    visited.add(node);
    
    for (const [neighbor, weight] of graph[node]) {
      const newDist = distances[node] + weight;
      if (newDist < distances[neighbor]) {
        distances[neighbor] = newDist;
        pq.enqueue(neighbor, newDist);
      }
    }
  }
  
  return distances;
}`
  },
  'prim': {
    id: 'prim',
    name: '最小生成树',
    category: 'graph',
    modelStyle: 'helix',  // 螺旋体 - 生成树算法
    dataType: 'array',
    defaultData: [0, 1, 2, 3, 4],
    description: {
      brief: 'Prim算法用于求加权无向图的最小生成树。从一个顶点开始，贪心地选择权重最小的边扩展。',
      timeComplexity: 'O((V+E) log V)',
      spaceComplexity: 'O(V)',
      useCases: ['网络布线', '管道铺设', '电路设计', '聚类分析'],
      pros: ['最优化总边权', '贪心高效', '适合稠密图'],
      cons: ['需要优先队列', '不适合有向图'],
      summary: 'Prim算法和Kruskal是最小生成树的两大经典。Prim适合稠密图，Kruskal适合稀疏图。网络布线、电路设计、聚类分析的基础。'
    },
    code: `// Prim 最小生成树算法
function prim(graph) {
  const mst = [];
  const visited = new Set();
  const edges = new PriorityQueue();
  
  // 从节点0开始
  visited.add(0);
  for (const [neighbor, weight] of graph[0]) {
    edges.enqueue({ from: 0, to: neighbor, weight }, weight);
  }
  
  while (!edges.isEmpty() && mst.length < graph.length - 1) {
    const { from, to, weight } = edges.dequeue();
    
    if (visited.has(to)) continue;
    
    visited.add(to);
    mst.push({ from, to, weight });
    
    for (const [neighbor, w] of graph[to]) {
      if (!visited.has(neighbor)) {
        edges.enqueue({ from: to, to: neighbor, weight: w }, w);
      }
    }
  }
  
  return mst;
}`
  },
  'topological': {
    id: 'topological',
    name: '拓扑排序',
    category: 'graph',
    modelStyle: 'vortex',  // 漩涡 - 依赖关系算法
    dataType: 'array',
    defaultData: [0, 1, 2, 3, 4, 5],
    description: {
      brief: '拓扑排序对有向无环图(DAG)的节点进行线性排序，保证所有依赖关系顺序正确。',
      timeComplexity: 'O(V + E)',
      spaceComplexity: 'O(V)',
      useCases: ['任务调度', '课程安排', '编译依赖', '工程管理'],
      pros: ['解决依赖问题', '高效线性', '应用广泛'],
      cons: ['只适用DAG', '无法处理环'],
      summary: '拓扑排序解决依赖关系排序问题。课程表排课、编译顺序、任务调度都依赖它。Kahn和DFS是两种常见实现方式。'
    },
    code: `// 拓扑排序 (Kahn算法)
function topologicalSort(graph) {
  const inDegree = new Array(graph.length).fill(0);
  const queue = [];
  const result = [];
  
  // 计算入度
  for (const neighbors of graph) {
    for (const neighbor of neighbors) {
      inDegree[neighbor]++;
    }
  }
  
  // 入度为0的节点入队
  for (let i = 0; i < inDegree.length; i++) {
    if (inDegree[i] === 0) queue.push(i);
  }
  
  while (queue.length > 0) {
    const node = queue.shift();
    result.push(node);
    
    for (const neighbor of graph[node]) {
      inDegree[neighbor]--;
      if (inDegree[neighbor] === 0) {
        queue.push(neighbor);
      }
    }
  }
  
  return result.length === graph.length ? result : [];
}`
  },

  // ========== 动态规划 ==========
  'fibonacci': {
    id: 'fibonacci',
    name: '斐波那契数列',
    category: 'dp',
    modelStyle: 'crystal',
    dataType: 'array',
    defaultData: [0, 1, 1, 2, 3, 5, 8, 13],
    description: {
      brief: '斐波那契数列是动态规划的入门经典，每个数等于前两个数之和。广泛应用于数学和计算机科学。',
      timeComplexity: 'O(n)',
      spaceComplexity: 'O(1)',
      useCases: ['数学建模', '算法入门', '动态规划教学', '自然规律'],
      pros: ['简单易懂', 'DP入门典范', '优化空间'],
      cons: ['大数会溢出', '应用场景有限'],
      summary: '旐波那契是动态规划的入门经典，展示了重叠子问题和最优子结构。从递归到记忆化再到遍历DP，是理解DP化过程的最佳案例。'
    },
    code: {
      javascript: `// 斐波那契数列 - 动态规划
function fibonacci(n) {
  if (n <= 1) return n;
  let prev = 0, curr = 1;
  for (let i = 2; i <= n; i++) {
    [prev, curr] = [curr, prev + curr];
  }
  return curr;
}`,
      python: `# 斐波那契数列 - 动态规划
def fibonacci(n):
    if n <= 1:
        return n
    prev, curr = 0, 1
    for _ in range(2, n + 1):
        prev, curr = curr, prev + curr
    return curr`,
      cpp: `// 斐波那契数列 - 动态规划
int fibonacci(int n) {
    if (n <= 1) return n;
    int prev = 0, curr = 1;
    for (int i = 2; i <= n; i++) {
        int temp = curr;
        curr = prev + curr;
        prev = temp;
    }
    return curr;
}`
    }
  },
  'knapsack': {
    id: 'knapsack',
    name: '背包问题',
    category: 'dp',
    modelStyle: 'dataBlock',
    dataType: 'array',
    defaultData: [2, 3, 4, 5],
    description: {
      brief: '0/1背包问题：给定一组物品的重量和价值，在容量限制下选择价值最大的物品组合。',
      timeComplexity: 'O(n × W)',
      spaceComplexity: 'O(n × W)',
      useCases: ['资源分配', '投资组合', '货物装载', '项目选择'],
      pros: ['最优化问题', 'DP经典', '广泛应用'],
      cons: ['空间复杂度高', '无法处理分数'],
      summary: '0/1背包是DP中最具代表性的问题，是很多资源分配问题的抛象。状态转移方程的设计是核心。空间可优化到O(W)。'
    },
    code: {
      javascript: `// 0/1背包问题
function knapsack(weights, values, capacity) {
  const n = weights.length;
  const dp = Array(n + 1).fill(null)
    .map(() => Array(capacity + 1).fill(0));
  for (let i = 1; i <= n; i++) {
    for (let w = 1; w <= capacity; w++) {
      if (weights[i-1] <= w) {
        dp[i][w] = Math.max(dp[i-1][w],
          dp[i-1][w-weights[i-1]] + values[i-1]);
      } else {
        dp[i][w] = dp[i-1][w];
      }
    }
  }
  return dp[n][capacity];
}`,
      python: `# 0/1背包问题
def knapsack(weights, values, capacity):
    n = len(weights)
    dp = [[0] * (capacity + 1) for _ in range(n + 1)]
    for i in range(1, n + 1):
        for w in range(1, capacity + 1):
            if weights[i-1] <= w:
                dp[i][w] = max(dp[i-1][w],
                    dp[i-1][w-weights[i-1]] + values[i-1])
            else:
                dp[i][w] = dp[i-1][w]
    return dp[n][capacity]`,
      cpp: `// 0/1背包问题
int knapsack(vector<int>& weights, vector<int>& values, int capacity) {
    int n = weights.size();
    vector<vector<int>> dp(n+1, vector<int>(capacity+1, 0));
    for (int i = 1; i <= n; i++) {
        for (int w = 1; w <= capacity; w++) {
            if (weights[i-1] <= w) {
                dp[i][w] = max(dp[i-1][w],
                    dp[i-1][w-weights[i-1]] + values[i-1]);
            } else {
                dp[i][w] = dp[i-1][w];
            }
        }
    }
    return dp[n][capacity];
}`
    }
  },
  'lcs': {
    id: 'lcs',
    name: '最长公共子序列',
    category: 'dp',
    modelStyle: 'cube',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6],
    description: {
      brief: '最长公共子序列(LCS)找出两个序列中最长的公共子序列，不要求连续。广泛用于文本比对。',
      timeComplexity: 'O(m × n)',
      spaceComplexity: 'O(m × n)',
      useCases: ['文本Diff', 'DNA序列比对', '版本控制', '拼写检查'],
      pros: ['DP经典', '应用广泛', '性能稳定'],
      cons: ['空间复杂度高', '需要空间优化'],
      summary: 'LCS是文本Diff、版本控制的核心算法。与Git的diff、DNA序列比对密切相关。空间可优化到O(min(m,n))。'
    },
    code: {
      javascript: `// 最长公共子序列
function lcs(text1, text2) {
  const m = text1.length, n = text2.length;
  const dp = Array(m+1).fill(null).map(() => Array(n+1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (text1[i-1] === text2[j-1]) {
        dp[i][j] = dp[i-1][j-1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i-1][j], dp[i][j-1]);
      }
    }
  }
  return dp[m][n];
}`,
      python: `# 最长公共子序列
def lcs(text1, text2):
    m, n = len(text1), len(text2)
    dp = [[0] * (n + 1) for _ in range(m + 1)]
    for i in range(1, m + 1):
        for j in range(1, n + 1):
            if text1[i-1] == text2[j-1]:
                dp[i][j] = dp[i-1][j-1] + 1
            else:
                dp[i][j] = max(dp[i-1][j], dp[i][j-1])
    return dp[m][n]`,
      cpp: `// 最长公共子序列
int lcs(string text1, string text2) {
    int m = text1.size(), n = text2.size();
    vector<vector<int>> dp(m+1, vector<int>(n+1, 0));
    for (int i = 1; i <= m; i++) {
        for (int j = 1; j <= n; j++) {
            if (text1[i-1] == text2[j-1]) {
                dp[i][j] = dp[i-1][j-1] + 1;
            } else {
                dp[i][j] = max(dp[i-1][j], dp[i][j-1]);
            }
        }
    }
    return dp[m][n];
}`
    }
  },
  'coin-change': {
    id: 'coin-change',
    name: '零钱兑换',
    category: 'dp',
    modelStyle: 'wave',  // 波浪体 - 动态规划
    dataType: 'array',
    defaultData: [1, 2, 5],
    description: {
      brief: '零钱兑换问题：给定不同面额的硬币和目标金额，求凑成该金额的最少硬币数。',
      timeComplexity: 'O(n × amount)',
      spaceComplexity: 'O(amount)',
      useCases: ['找零问题', '货币系统', '资源分配', '购物结算'],
      pros: ['DP经典', '广泛应用', '优化空间'],
      cons: ['无解时返回-1', '只能求数量'],
      summary: '零钱兑换是完全背包问题的变体，理解无限元素可重复使用的状态转移。实际生活中的找零问题、商品组合都是其应用。'
    },
    code: {
      javascript: `// 零钱兑换
function coinChange(coins, amount) {
  const dp = new Array(amount + 1).fill(Infinity);
  dp[0] = 0;
  for (let i = 1; i <= amount; i++) {
    for (const coin of coins) {
      if (coin <= i && dp[i - coin] !== Infinity) {
        dp[i] = Math.min(dp[i], dp[i - coin] + 1);
      }
    }
  }
  return dp[amount] === Infinity ? -1 : dp[amount];
}`,
      python: `# 零钱兑换
def coin_change(coins, amount):
    dp = [float('inf')] * (amount + 1)
    dp[0] = 0
    for i in range(1, amount + 1):
        for coin in coins:
            if coin <= i and dp[i - coin] != float('inf'):
                dp[i] = min(dp[i], dp[i - coin] + 1)
    return dp[amount] if dp[amount] != float('inf') else -1`,
      cpp: `// 零钱兑换
int coinChange(vector<int>& coins, int amount) {
    vector<int> dp(amount + 1, INT_MAX);
    dp[0] = 0;
    for (int i = 1; i <= amount; i++) {
        for (int coin : coins) {
            if (coin <= i && dp[i - coin] != INT_MAX) {
                dp[i] = min(dp[i], dp[i - coin] + 1);
            }
        }
    }
    return dp[amount] == INT_MAX ? -1 : dp[amount];
}`
    }
  },
  'lis': {
    id: 'lis',
    name: '最长递增子序列',
    category: 'dp',
    modelStyle: 'wave',  // 波浪体 - 序列算法
    dataType: 'array',
    defaultData: [10, 9, 2, 5, 3, 7, 101, 18],
    description: {
      brief: '最长递增子序列(LIS)找出一个序列中严格递增的最长子序列长度。可以用DP或二分查找优化。',
      timeComplexity: 'O(n²) / O(n log n)',
      spaceComplexity: 'O(n)',
      useCases: ['序列分析', '股票走势', '数据处理', '算法竞赛'],
      pros: ['DP经典', '可优化', '应用广泛'],
      cons: ['普速DP慢', '需要二分优化'],
      summary: 'LIS是DP经典问题，普速O(n²)可用二分优化到O(n log n)。广泛应用于序列分析、股票走势、算法竞赛。'
    },
    code: {
      javascript: `// 最长递增子序列
function lengthOfLIS(nums) {
  if (nums.length === 0) return 0;
  const dp = new Array(nums.length).fill(1);
  let maxLen = 1;
  for (let i = 1; i < nums.length; i++) {
    for (let j = 0; j < i; j++) {
      if (nums[i] > nums[j]) {
        dp[i] = Math.max(dp[i], dp[j] + 1);
      }
    }
    maxLen = Math.max(maxLen, dp[i]);
  }
  return maxLen;
}`,
      python: `# 最长递增子序列
def length_of_lis(nums):
    if not nums:
        return 0
    dp = [1] * len(nums)
    for i in range(1, len(nums)):
        for j in range(i):
            if nums[i] > nums[j]:
                dp[i] = max(dp[i], dp[j] + 1)
    return max(dp)`,
      cpp: `// 最长递增子序列
int lengthOfLIS(vector<int>& nums) {
    if (nums.empty()) return 0;
    vector<int> dp(nums.size(), 1);
    int maxLen = 1;
    for (int i = 1; i < nums.size(); i++) {
        for (int j = 0; j < i; j++) {
            if (nums[i] > nums[j]) {
                dp[i] = max(dp[i], dp[j] + 1);
            }
        }
        maxLen = max(maxLen, dp[i]);
    }
    return maxLen;
}`
    }
  },

  // ========== 经典算法 ==========
  'hanoi': {
    id: 'hanoi',
    name: '汉诺塔',
    category: 'classic',
    modelStyle: 'tower',  // 塔状 - 堆叠结构
    dataType: 'array',
    defaultData: [3, 2, 1],
    description: {
      brief: '汉诺塔是经典递归问题，将n个圆盘从一根柱子移到另一根，每次只能移动一个且大盘不能压小盘。',
      timeComplexity: 'O(2ⁿ)',
      spaceComplexity: 'O(n)',
      useCases: ['递归入门', '分治思想', '教学演示', '算法思维'],
      pros: ['递归典范', '逻辑清晰', '优雅简洁'],
      cons: ['指数复杂度', '大n不实用'],
      summary: '汉诺塔是递归算法的经典入门，展示了分治思想的精髓。虽然实际应用有限，但是理解递归思维的绝佳教学案例。'
    },
    code: {
      javascript: `// 汉诺塔
function hanoi(n, from, to, aux) {
  if (n === 1) {
    console.log(\`Move disk 1 from \${from} to \${to}\`);
    return;
  }
  hanoi(n - 1, from, aux, to);
  console.log(\`Move disk \${n} from \${from} to \${to}\`);
  hanoi(n - 1, aux, to, from);
}
// hanoi(3, 'A', 'C', 'B')`,
      python: `# 汉诺塔
def hanoi(n, from_rod, to_rod, aux_rod):
    if n == 1:
        print(f"Move disk 1 from {from_rod} to {to_rod}")
        return
    hanoi(n - 1, from_rod, aux_rod, to_rod)
    print(f"Move disk {n} from {from_rod} to {to_rod}")
    hanoi(n - 1, aux_rod, to_rod, from_rod)

# hanoi(3, 'A', 'C', 'B')`,
      cpp: `// 汉诺塔
void hanoi(int n, char from, char to, char aux) {
    if (n == 1) {
        cout << "Move disk 1 from " << from << " to " << to << endl;
        return;
    }
    hanoi(n - 1, from, aux, to);
    cout << "Move disk " << n << " from " << from << " to " << to << endl;
    hanoi(n - 1, aux, to, from);
}
// hanoi(3, 'A', 'C', 'B');`
    }
  },
  'n-queens': {
    id: 'n-queens',
    name: 'N皇后问题',
    category: 'classic',
    modelStyle: 'crystal',
    dataType: 'array',
    defaultData: [0, 0, 0, 0],
    description: {
      brief: 'N皇后问题：在N×N棋盘上放置N个皇后，使得任何两个皇后都不能互相攻击。回溯算法经典。',
      timeComplexity: 'O(N!)',
      spaceComplexity: 'O(N)',
      useCases: ['回溯教学', '约束满足', '组合搜索', '算法竞赛'],
      pros: ['回溯典范', '逻辑清晰', '教学价值高'],
      cons: ['复杂度高', '大N计算慢'],
      summary: 'N皇后是回溯算法的经典代表，約束满足问题的典型案例。掌握其剪枝技巧对解决其他组合搜索问题很有帮助。'
    },
    code: {
      javascript: `// N皇后问题
function solveNQueens(n) {
  const result = [];
  const board = Array(n).fill(-1);
  
  function isValid(row, col) {
    for (let i = 0; i < row; i++) {
      if (board[i] === col || Math.abs(board[i] - col) === row - i)
        return false;
    }
    return true;
  }
  
  function backtrack(row) {
    if (row === n) { result.push([...board]); return; }
    for (let col = 0; col < n; col++) {
      if (isValid(row, col)) {
        board[row] = col;
        backtrack(row + 1);
        board[row] = -1;
      }
    }
  }
  backtrack(0);
  return result;
}`,
      python: `# N皇后问题
def solve_n_queens(n):
    result = []
    board = [-1] * n
    
    def is_valid(row, col):
        for i in range(row):
            if board[i] == col or abs(board[i] - col) == row - i:
                return False
        return True
    
    def backtrack(row):
        if row == n:
            result.append(board[:])
            return
        for col in range(n):
            if is_valid(row, col):
                board[row] = col
                backtrack(row + 1)
                board[row] = -1
    
    backtrack(0)
    return result`,
      cpp: `// N皇后问题
vector<vector<int>> solveNQueens(int n) {
    vector<vector<int>> result;
    vector<int> board(n, -1);
    
    function<bool(int, int)> isValid = [&](int row, int col) {
        for (int i = 0; i < row; i++) {
            if (board[i] == col || abs(board[i] - col) == row - i)
                return false;
        }
        return true;
    };
    
    function<void(int)> backtrack = [&](int row) {
        if (row == n) { result.push_back(board); return; }
        for (int col = 0; col < n; col++) {
            if (isValid(row, col)) {
                board[row] = col;
                backtrack(row + 1);
                board[row] = -1;
            }
        }
    };
    backtrack(0);
    return result;
}`
    }
  },
  'maze': {
    id: 'maze',
    name: '迷宫求解',
    category: 'classic',
    modelStyle: 'node',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    description: {
      brief: '迷宫求解使用DFS或BFS找出从起点到终点的路径。DFS找到一条路径，BFS找最短路径。',
      timeComplexity: 'O(m × n)',
      spaceComplexity: 'O(m × n)',
      useCases: ['游戏寻路', '机器人导航', '路径规划', '算法教学'],
      pros: ['理解简单', '应用广泛', '可视化强'],
      cons: ['可能走远路', '复杂地图慢'],
      summary: '迷宫求解是搜索算法的直观应用。DFS找到一条路径，BFS找最短路径。游戏寻路、机器人导航、路线规划都是其实际应用。'
    },
    code: `// 迷宫求解 - DFS
function solveMaze(maze, start, end) {
  const rows = maze.length;
  const cols = maze[0].length;
  const path = [];
  const visited = new Set();
  
  function dfs(r, c) {
    if (r < 0 || r >= rows || c < 0 || c >= cols) return false;
    if (maze[r][c] === 1) return false;  // 墙
    if (visited.has(\`\${r},\${c}\`)) return false;
    
    visited.add(\`\${r},\${c}\`);
    path.push([r, c]);
    
    if (r === end[0] && c === end[1]) return true;
    
    // 尝试四个方向
    if (dfs(r + 1, c) || dfs(r - 1, c) || 
        dfs(r, c + 1) || dfs(r, c - 1)) {
      return true;
    }
    
    path.pop();
    return false;
  }
  
  dfs(start[0], start[1]);
  return path;
}`
  },
  'sudoku': {
    id: 'sudoku',
    name: '数独求解',
    category: 'classic',
    modelStyle: 'cube',
    dataType: 'array',
    defaultData: [5, 3, 0, 0, 7, 0, 0, 0, 0],
    description: {
      brief: '数独求解使用回溯算法，在空格处尝试1-9，检查行、列、宫的约束，递归求解。',
      timeComplexity: 'O(9^m), m为空格数',
      spaceComplexity: 'O(m)',
      useCases: ['数独游戏', '约束满足', '回溯演示', '智力训练'],
      pros: ['回溯典型', '规则明确', '实用性强'],
      cons: ['复杂度高', '难题计算慢'],
      summary: '数独求解展示了回溯算法处理约束满足问题的能力。明确的规则和有限的搜索空间使其成为回溯算法的完美教学案例。'
    },
    code: `// 数独求解
function solveSudoku(board) {
  function isValid(row, col, num) {
    for (let i = 0; i < 9; i++) {
      if (board[row][i] === num) return false;
      if (board[i][col] === num) return false;
    }
    
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (board[boxRow + i][boxCol + j] === num) return false;
      }
    }
    return true;
  }
  
  function solve() {
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (board[r][c] === 0) {
          for (let num = 1; num <= 9; num++) {
            if (isValid(r, c, num)) {
              board[r][c] = num;
              if (solve()) return true;
              board[r][c] = 0;
            }
          }
          return false;
        }
      }
    }
    return true;
  }
  
  solve();
  return board;
}`
  },
  'permutation': {
    id: 'permutation',
    name: '全排列',
    category: 'classic',
    modelStyle: 'hexagon',
    dataType: 'array',
    defaultData: [1, 2, 3],
    description: {
      brief: '全排列使用回溯算法生成所有可能的排列组合。是组合数学的基础问题。',
      timeComplexity: 'O(n!)',
      spaceComplexity: 'O(n)',
      useCases: ['组合数学', '密码生成', '调度问题', '算法竞赛'],
      pros: ['回溯典范', '逻辑清晰', '应用广泛'],
      cons: ['阶乘复杂度', '大n爆炸'],
      summary: '全排列是组合数学的基础问题，回溯算法的典型应用。密码破解、调度问题、组合优化都涉及排列生成。'
    },
    code: {
      javascript: `// 全排列
function permute(nums) {
  const result = [];
  function backtrack(path, used) {
    if (path.length === nums.length) {
      result.push([...path]);
      return;
    }
    for (let i = 0; i < nums.length; i++) {
      if (used[i]) continue;
      path.push(nums[i]);
      used[i] = true;
      backtrack(path, used);
      path.pop();
      used[i] = false;
    }
  }
  backtrack([], new Array(nums.length).fill(false));
  return result;
}`,
      python: `# 全排列
def permute(nums):
    result = []
    def backtrack(path, used):
        if len(path) == len(nums):
            result.append(path[:])
            return
        for i in range(len(nums)):
            if used[i]:
                continue
            path.append(nums[i])
            used[i] = True
            backtrack(path, used)
            path.pop()
            used[i] = False
    backtrack([], [False] * len(nums))
    return result`,
      cpp: `// 全排列
vector<vector<int>> permute(vector<int>& nums) {
    vector<vector<int>> result;
    vector<bool> used(nums.size(), false);
    vector<int> path;
    
    function<void()> backtrack = [&]() {
        if (path.size() == nums.size()) {
            result.push_back(path);
            return;
        }
        for (int i = 0; i < nums.size(); i++) {
            if (used[i]) continue;
            path.push_back(nums[i]);
            used[i] = true;
            backtrack();
            path.pop_back();
            used[i] = false;
        }
    };
    backtrack();
    return result;
}`
    }
  },

  // ========== 字符串算法 ==========
  'kmp': {
    id: 'kmp',
    name: 'KMP匹配',
    category: 'string',
    modelStyle: 'ribbon',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6, 7, 8],
    description: {
      brief: 'KMP算法是高效的字符串匹配算法，通过预处理模式串的前缀函数，避免重复比较。',
      timeComplexity: 'O(n + m)',
      spaceComplexity: 'O(m)',
      useCases: ['文本搜索', '字符串匹配', '编辑器查找', '数据分析'],
      pros: ['线性复杂度', '不回退主串', '高效稳定'],
      cons: ['实现复杂', '需预处理'],
      summary: 'KMP是字符串匹配的里程碑算法，巧妙利用已匹配信息避免重复比较。文本编辑器的查找功能、数据处理都依赖它。'
    },
    code: {
      javascript: `// KMP字符串匹配
function kmpSearch(text, pattern) {
  const lps = buildLPS(pattern);
  let i = 0, j = 0;
  while (i < text.length) {
    if (text[i] === pattern[j]) {
      i++; j++;
      if (j === pattern.length) return i - j;
    } else if (j > 0) {
      j = lps[j - 1];
    } else {
      i++;
    }
  }
  return -1;
}

function buildLPS(pattern) {
  const lps = [0];
  let len = 0, i = 1;
  while (i < pattern.length) {
    if (pattern[i] === pattern[len]) lps[i++] = ++len;
    else if (len > 0) len = lps[len - 1];
    else lps[i++] = 0;
  }
  return lps;
}`,
      python: `# KMP字符串匹配
def kmp_search(text, pattern):
    lps = build_lps(pattern)
    i = j = 0
    while i < len(text):
        if text[i] == pattern[j]:
            i += 1
            j += 1
            if j == len(pattern):
                return i - j
        elif j > 0:
            j = lps[j - 1]
        else:
            i += 1
    return -1

def build_lps(pattern):
    lps = [0] * len(pattern)
    length, i = 0, 1
    while i < len(pattern):
        if pattern[i] == pattern[length]:
            length += 1
            lps[i] = length
            i += 1
        elif length > 0:
            length = lps[length - 1]
        else:
            lps[i] = 0
            i += 1
    return lps`,
      cpp: `// KMP字符串匹配
vector<int> buildLPS(const string& pattern) {
    vector<int> lps(pattern.size(), 0);
    int len = 0, i = 1;
    while (i < pattern.size()) {
        if (pattern[i] == pattern[len]) lps[i++] = ++len;
        else if (len > 0) len = lps[len - 1];
        else lps[i++] = 0;
    }
    return lps;
}

int kmpSearch(const string& text, const string& pattern) {
    vector<int> lps = buildLPS(pattern);
    int i = 0, j = 0;
    while (i < text.size()) {
        if (text[i] == pattern[j]) {
            i++; j++;
            if (j == pattern.size()) return i - j;
        } else if (j > 0) j = lps[j - 1];
        else i++;
    }
    return -1;
}`
    }
  },
  'rabin-karp': {
    id: 'rabin-karp',
    name: 'Rabin-Karp',
    category: 'string',
    modelStyle: 'ribbon',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6, 7, 8],
    description: {
      brief: 'Rabin-Karp使用哈希函数进行字符串匹配，通过滚动哈希快速比较窗口字符串。',
      timeComplexity: '平均O(n+m)，最坏O(nm)',
      spaceComplexity: 'O(1)',
      useCases: ['多模式匹配', '版权检测', '文本搜索', '重复检测'],
      pros: ['多模式高效', '滚动哈希', '平均复杂度低'],
      cons: ['可能哈希冲突', '最坏情况慢'],
      summary: 'Rabin-Karp的滚动哈希思想巧妙，特别适合多模式匹配。论文查重、版权检测、网页搜索的常用算法。'
    },
    code: `// Rabin-Karp 字符串匹配
function rabinKarp(text, pattern) {
  const d = 256;  // 字符集大小
  const q = 101;  // 质数
  const m = pattern.length;
  const n = text.length;
  
  let pHash = 0;  // pattern hash
  let tHash = 0;  // text window hash
  let h = 1;
  
  // h = d^(m-1) % q
  for (let i = 0; i < m - 1; i++) {
    h = (h * d) % q;
  }
  
  // 计算初始哈希值
  for (let i = 0; i < m; i++) {
    pHash = (d * pHash + pattern.charCodeAt(i)) % q;
    tHash = (d * tHash + text.charCodeAt(i)) % q;
  }
  
  // 滑动窗口
  for (let i = 0; i <= n - m; i++) {
    if (pHash === tHash) {
      if (text.substring(i, i + m) === pattern) {
        return i;
      }
    }
    
    if (i < n - m) {
      tHash = (d * (tHash - text.charCodeAt(i) * h) + 
               text.charCodeAt(i + m)) % q;
      if (tHash < 0) tHash += q;
    }
  }
  return -1;
}`
  },
  'edit-distance': {
    id: 'edit-distance',
    name: '编辑距离',
    category: 'string',
    modelStyle: 'crystal',
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5],
    description: {
      brief: '编辑距离计算将一个字符串转换为另一个所需的最少操作次数（插入、删除、替换）。',
      timeComplexity: 'O(m × n)',
      spaceComplexity: 'O(m × n)',
      useCases: ['拼写纠错', '模糊搜索', 'DNA比对', '文本Diff'],
      pros: ['DP经典', '广泛应用', '可优化空间'],
      cons: ['空间复杂度高', '长字符串慢'],
      summary: '编辑距离是文本相似度计算的核心。拼写纠错、模糊搜索、DNA序列比对广泛使用。DP经典问题之一。'
    },
    code: `// 编辑距离 (Levenshtein Distance)
function editDistance(word1, word2) {
  const m = word1.length;
  const n = word2.length;
  
  const dp = Array(m + 1).fill(null)
    .map(() => Array(n + 1).fill(0));
  
  // 初始化边界
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (word1[i - 1] === word2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],      // 删除
          dp[i][j - 1],      // 插入
          dp[i - 1][j - 1]   // 替换
        );
      }
    }
  }
  
  return dp[m][n];
}`
  },

  // ========== 数学算法 ==========
  'gcd': {
    id: 'gcd',
    name: '最大公约数',
    category: 'math',
    modelStyle: 'gear',
    dataType: 'array',
    defaultData: [48, 18],
    description: {
      brief: '欧几里得算法通过取模运算高效计算两个数的最大公约数(GCD)，是最古老的算法之一。',
      timeComplexity: 'O(log min(a,b))',
      spaceComplexity: 'O(1)',
      useCases: ['简化分数', '密码学', '同步问题', '数论'],
      pros: ['非常高效', '历史悠久', '实现简单'],
      cons: ['只处理整数', '负数需特殊处理'],
      summary: '欧几里得算法是数学中最古老的算法之一，简单而高效。密码学、简化分数、同步问题的基础。扩展欧几里得算法更强大。'
    },
    code: {
      javascript: `// 最大公约数 - 欧几里得算法
function gcd(a, b) {
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return a;
}

// 最小公倍数
function lcm(a, b) {
  return (a * b) / gcd(a, b);
}`,
      python: `# 最大公约数 - 欧几里得算法
def gcd(a, b):
    while b != 0:
        a, b = b, a % b
    return a

# 最小公倍数
def lcm(a, b):
    return (a * b) // gcd(a, b)`,
      cpp: `// 最大公约数 - 欧几里得算法
int gcd(int a, int b) {
    while (b != 0) {
        int temp = b;
        b = a % b;
        a = temp;
    }
    return a;
}

// 最小公倍数
int lcm(int a, int b) {
    return (a * b) / gcd(a, b);
}`
    }
  },
  'sieve': {
    id: 'sieve',
    name: '素数筛法',
    category: 'math',
    modelStyle: 'prism',
    dataType: 'array',
    defaultData: [2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: {
      brief: '埃拉托斯特尼筛法高效筛选出指定范围内的所有素数，逐步排除合数。',
      timeComplexity: 'O(n log log n)',
      spaceComplexity: 'O(n)',
      useCases: ['素数生成', '密码学', '数论', '算法竞赛'],
      pros: ['批量求素数最优', '实现简单', '应用广泛'],
      cons: ['占用内存', '大范围需优化'],
      summary: '埃拉托斯特尼筛法是批量求素数的最优解法。筛法思想在算法中广泛应用。密码学、数论、算法竞赛的重要工具。'
    },
    code: {
      javascript: `// 埃拉托斯特尼筛法
function sieveOfEratosthenes(n) {
  const isPrime = new Array(n + 1).fill(true);
  isPrime[0] = isPrime[1] = false;
  for (let i = 2; i * i <= n; i++) {
    if (isPrime[i]) {
      for (let j = i * i; j <= n; j += i) {
        isPrime[j] = false;
      }
    }
  }
  return isPrime.map((v, i) => v ? i : -1).filter(v => v > 0);
}`,
      python: `# 埃拉托斯特尼筛法
def sieve_of_eratosthenes(n):
    is_prime = [True] * (n + 1)
    is_prime[0] = is_prime[1] = False
    for i in range(2, int(n**0.5) + 1):
        if is_prime[i]:
            for j in range(i*i, n + 1, i):
                is_prime[j] = False
    return [i for i in range(n + 1) if is_prime[i]]`,
      cpp: `// 埃拉托斯特尼筛法
vector<int> sieveOfEratosthenes(int n) {
    vector<bool> isPrime(n + 1, true);
    isPrime[0] = isPrime[1] = false;
    for (int i = 2; i * i <= n; i++) {
        if (isPrime[i]) {
            for (int j = i * i; j <= n; j += i)
                isPrime[j] = false;
        }
    }
    vector<int> primes;
    for (int i = 2; i <= n; i++)
        if (isPrime[i]) primes.push_back(i);
    return primes;
}`
    }
  },
  'power': {
    id: 'power',
    name: '快速幂',
    category: 'math',
    modelStyle: 'star',
    dataType: 'array',
    defaultData: [2, 10],
    description: {
      brief: '快速幂通过二进制分解指数，将O(n)的幂运算优化到O(log n)。是数论和密码学的基础算法。',
      timeComplexity: 'O(log n)',
      spaceComplexity: 'O(1)',
      useCases: ['大数幂运算', 'RSA加密', '矩阵快速幂', '算法竞赛'],
      pros: ['对数复杂度', '实现简单', '广泛应用'],
      cons: ['大数运算需处理溢出', '需理解二进制'],
      summary: '快速幂是数论和密码学的基础算法。RSA加密依赖它计算大数幂模。矩阵快速幂是其扩展，用于加速递推。'
    },
    code: {
      javascript: `// 快速幂算法
function power(base, exp) {
  let result = 1;
  while (exp > 0) {
    if (exp % 2 === 1) result *= base;
    base *= base;
    exp = Math.floor(exp / 2);
  }
  return result;
}`,
      python: `# 快速幂算法
def power(base, exp):
    result = 1
    while exp > 0:
        if exp % 2 == 1:
            result *= base
        base *= base
        exp //= 2
    return result`,
      cpp: `// 快速幂算法
long long power(long long base, int exp) {
    long long result = 1;
    while (exp > 0) {
        if (exp % 2 == 1) result *= base;
        base *= base;
        exp /= 2;
    }
    return result;
}`
    }
  },

  // ========== 贪心算法 ==========
  'activity-selection': {
    id: 'activity-selection',
    name: '活动选择',
    category: 'greedy',
    modelStyle: 'wave',  // 波浪体 - 时间线算法
    dataType: 'array',
    defaultData: [1, 2, 3, 4, 5, 6],
    description: {
      brief: '活动选择问题是经典的贪心算法应用，目标是在一组活动中选择最大数量的不重叠活动。',
      timeComplexity: 'O(n log n)',
      spaceComplexity: 'O(1)',
      useCases: ['会议室调度', '任务分配', '资源管理'],
      pros: ['简单高效', '时间复杂度低'],
      cons: ['仅适用于特定问题结构'],
      summary: '活动选择问题通过贪心策略，每次选择结束时间最早的活动，保证剩余时间最大化。'
    },
    code: {
      javascript: `// 活动选择算法
function activitySelection(start, finish) {
    const n = start.length;
    const activities = [];
    
    // 创建活动对象并按结束时间排序
    for (let i = 0; i < n; i++) {
        activities.push({ start: start[i], finish: finish[i], index: i });
    }
    activities.sort((a, b) => a.finish - b.finish);
    
    const selected = [activities[0]];
    let lastFinish = activities[0].finish;
    
    for (let i = 1; i < n; i++) {
        if (activities[i].start >= lastFinish) {
            selected.push(activities[i]);
            lastFinish = activities[i].finish;
        }
    }
    
    return selected;
}`,
      python: `# 活动选择算法
def activity_selection(start, finish):
    n = len(start)
    activities = sorted(zip(start, finish, range(n)), key=lambda x: x[1])
    
    selected = [activities[0]]
    last_finish = activities[0][1]
    
    for i in range(1, n):
        if activities[i][0] >= last_finish:
            selected.append(activities[i])
            last_finish = activities[i][1]
    
    return selected`,
      cpp: `// 活动选择算法
vector<int> activitySelection(vector<int>& start, vector<int>& finish) {
    int n = start.size();
    vector<pair<int, int>> activities;
    for (int i = 0; i < n; i++) {
        activities.push_back({finish[i], i});
    }
    sort(activities.begin(), activities.end());
    
    vector<int> selected;
    selected.push_back(activities[0].second);
    int lastFinish = finish[activities[0].second];
    
    for (int i = 1; i < n; i++) {
        int idx = activities[i].second;
        if (start[idx] >= lastFinish) {
            selected.push_back(idx);
            lastFinish = finish[idx];
        }
    }
    return selected;
}`
    }
  },

  'huffman': {
    id: 'huffman',
    name: '霍夫曼编码',
    category: 'greedy',
    modelStyle: 'tower',  // 塔状 - 树形结构
    dataType: 'array',
    defaultData: [5, 9, 12, 13, 16, 45],
    description: {
      brief: '霍夫曼编码是一种用于无损数据压缩的贪心算法，通过构建最优二叉树实现编码。',
      timeComplexity: 'O(n log n)',
      spaceComplexity: 'O(n)',
      useCases: ['数据压缩', '文件编码', '通信传输'],
      pros: ['最优前缀编码', '压缩效率高'],
      cons: ['需要存储编码表', '解码复杂'],
      summary: '霍夫曼编码通过构建最小堆，每次合并两个最小频率节点，生成最优编码树。'
    },
    code: {
      javascript: `// 霍夫曼编码算法
class HuffmanNode {
    constructor(char, freq) {
        this.char = char;
        this.freq = freq;
        this.left = null;
        this.right = null;
    }
}

function buildHuffmanTree(chars, freqs) {
    const nodes = chars.map((c, i) => new HuffmanNode(c, freqs[i]));
    
    while (nodes.length > 1) {
        nodes.sort((a, b) => a.freq - b.freq);
        const left = nodes.shift();
        const right = nodes.shift();
        
        const parent = new HuffmanNode(null, left.freq + right.freq);
        parent.left = left;
        parent.right = right;
        nodes.push(parent);
    }
    
    return nodes[0];
}`,
      python: `# 霍夫曼编码算法
import heapq

class HuffmanNode:
    def __init__(self, char, freq):
        self.char = char
        self.freq = freq
        self.left = None
        self.right = None
    
    def __lt__(self, other):
        return self.freq < other.freq

def build_huffman_tree(chars, freqs):
    heap = [HuffmanNode(c, f) for c, f in zip(chars, freqs)]
    heapq.heapify(heap)
    
    while len(heap) > 1:
        left = heapq.heappop(heap)
        right = heapq.heappop(heap)
        
        parent = HuffmanNode(None, left.freq + right.freq)
        parent.left = left
        parent.right = right
        heapq.heappush(heap, parent)
    
    return heap[0]`,
      cpp: `// 霍夫曼编码算法
struct HuffmanNode {
    char ch;
    int freq;
    HuffmanNode *left, *right;
    HuffmanNode(char c, int f) : ch(c), freq(f), left(nullptr), right(nullptr) {}
};

struct Compare {
    bool operator()(HuffmanNode* a, HuffmanNode* b) {
        return a->freq > b->freq;
    }
};

HuffmanNode* buildHuffmanTree(string chars, vector<int>& freqs) {
    priority_queue<HuffmanNode*, vector<HuffmanNode*>, Compare> pq;
    for (int i = 0; i < chars.size(); i++) {
        pq.push(new HuffmanNode(chars[i], freqs[i]));
    }
    
    while (pq.size() > 1) {
        HuffmanNode* left = pq.top(); pq.pop();
        HuffmanNode* right = pq.top(); pq.pop();
        
        HuffmanNode* parent = new HuffmanNode('\\0', left->freq + right->freq);
        parent->left = left;
        parent->right = right;
        pq.push(parent);
    }
    return pq.top();
}`
    }
  },

  // ========== 数据结构补充 ==========
  'union-find': {
    id: 'union-find',
    name: '并查集',
    category: 'data-structure',
    modelStyle: 'vortex',  // 漩涡 - 集合合并
    dataType: 'array',
    defaultData: [0, 1, 2, 3, 4, 5, 6, 7],
    description: {
      brief: '并查集是一种树形数据结构，用于处理不交集的合并与查询问题。',
      timeComplexity: 'O(α(n)) 近似 O(1)',
      spaceComplexity: 'O(n)',
      useCases: ['连通性检测', '最小生成树', '网络分组'],
      pros: ['操作极快', '实现简单'],
      cons: ['不支持删除操作'],
      summary: '并查集通过路径压缩和按秩合并优化，实现近乎常数时间的合并和查找。'
    },
    code: {
      javascript: `// 并查集
class UnionFind {
    constructor(n) {
        this.parent = Array.from({length: n}, (_, i) => i);
        this.rank = new Array(n).fill(0);
    }
    
    find(x) {
        if (this.parent[x] !== x) {
            this.parent[x] = this.find(this.parent[x]); // 路径压缩
        }
        return this.parent[x];
    }
    
    union(x, y) {
        const px = this.find(x);
        const py = this.find(y);
        if (px === py) return false;
        
        // 按秩合并
        if (this.rank[px] < this.rank[py]) {
            this.parent[px] = py;
        } else if (this.rank[px] > this.rank[py]) {
            this.parent[py] = px;
        } else {
            this.parent[py] = px;
            this.rank[px]++;
        }
        return true;
    }
}`,
      python: `# 并查集
class UnionFind:
    def __init__(self, n):
        self.parent = list(range(n))
        self.rank = [0] * n
    
    def find(self, x):
        if self.parent[x] != x:
            self.parent[x] = self.find(self.parent[x])  # 路径压缩
        return self.parent[x]
    
    def union(self, x, y):
        px, py = self.find(x), self.find(y)
        if px == py:
            return False
        
        # 按秩合并
        if self.rank[px] < self.rank[py]:
            self.parent[px] = py
        elif self.rank[px] > self.rank[py]:
            self.parent[py] = px
        else:
            self.parent[py] = px
            self.rank[px] += 1
        return True`,
      cpp: `// 并查集
class UnionFind {
public:
    vector<int> parent, rank;
    
    UnionFind(int n) : parent(n), rank(n, 0) {
        iota(parent.begin(), parent.end(), 0);
    }
    
    int find(int x) {
        if (parent[x] != x) {
            parent[x] = find(parent[x]); // 路径压缩
        }
        return parent[x];
    }
    
    bool unite(int x, int y) {
        int px = find(x), py = find(y);
        if (px == py) return false;
        
        // 按秩合并
        if (rank[px] < rank[py]) swap(px, py);
        parent[py] = px;
        if (rank[px] == rank[py]) rank[px]++;
        return true;
    }
};`
    }
  },

  // ========== 图算法补充 ==========
  'kruskal': {
    id: 'kruskal',
    name: 'Kruskal最小生成树',
    category: 'graph',
    modelStyle: 'helix',  // 螺旋体 - 生成树算法
    dataType: 'array',
    defaultData: [0, 1, 2, 3, 4],
    description: {
      brief: 'Kruskal算法是一种求加权无向图的最小生成树的贪心算法。',
      timeComplexity: 'O(E log E)',
      spaceComplexity: 'O(V)',
      useCases: ['网络设计', '电路布线', '集群分析'],
      pros: ['适合稀疏图', '实现简单'],
      cons: ['需要排序边'],
      summary: 'Kruskal算法将边按权重排序，依次加入不形成环的最小边，直到生成树完成。'
    },
    code: {
      javascript: `// Kruskal最小生成树
function kruskal(n, edges) {
    // edges: [[u, v, weight], ...]
    edges.sort((a, b) => a[2] - b[2]);
    
    const parent = Array.from({length: n}, (_, i) => i);
    const find = (x) => parent[x] === x ? x : parent[x] = find(parent[x]);
    
    const mst = [];
    let totalWeight = 0;
    
    for (const [u, v, w] of edges) {
        const pu = find(u), pv = find(v);
        if (pu !== pv) {
            parent[pu] = pv;
            mst.push([u, v, w]);
            totalWeight += w;
            if (mst.length === n - 1) break;
        }
    }
    
    return { mst, totalWeight };
}`,
      python: `# Kruskal最小生成树
def kruskal(n, edges):
    # edges: [(u, v, weight), ...]
    edges.sort(key=lambda x: x[2])
    
    parent = list(range(n))
    
    def find(x):
        if parent[x] != x:
            parent[x] = find(parent[x])
        return parent[x]
    
    mst = []
    total_weight = 0
    
    for u, v, w in edges:
        pu, pv = find(u), find(v)
        if pu != pv:
            parent[pu] = pv
            mst.append((u, v, w))
            total_weight += w
            if len(mst) == n - 1:
                break
    
    return mst, total_weight`,
      cpp: `// Kruskal最小生成树
class Kruskal {
public:
    vector<int> parent;
    
    int find(int x) {
        return parent[x] == x ? x : parent[x] = find(parent[x]);
    }
    
    vector<tuple<int,int,int>> solve(int n, vector<tuple<int,int,int>>& edges) {
        sort(edges.begin(), edges.end(), 
             [](auto& a, auto& b) { return get<2>(a) < get<2>(b); });
        
        parent.resize(n);
        iota(parent.begin(), parent.end(), 0);
        
        vector<tuple<int,int,int>> mst;
        for (auto& [u, v, w] : edges) {
            int pu = find(u), pv = find(v);
            if (pu != pv) {
                parent[pu] = pv;
                mst.push_back({u, v, w});
                if (mst.size() == n - 1) break;
            }
        }
        return mst;
    }
};`
    }
  },

  // ========== 经典算法补充 ==========
  'closest-pair': {
    id: 'closest-pair',
    name: '最近点对',
    category: 'classic',
    modelStyle: 'vortex',  // 漩涡 - 分治聚合
    dataType: 'array',
    defaultData: [2, 3, 5, 1, 8, 4, 7, 6],
    description: {
      brief: '最近点对问题是计算几何中的经典分治算法，找到平面上距离最近的两个点。',
      timeComplexity: 'O(n log n)',
      spaceComplexity: 'O(n)',
      useCases: ['碰撞检测', '聚类分析', '图形学'],
      pros: ['时间复杂度优', '分治思想经典'],
      cons: ['实现较复杂'],
      summary: '最近点对通过分治策略，将点集分割并递归求解，然后合并时检查跨越分割线的点对。'
    },
    code: {
      javascript: `// 最近点对算法
function closestPair(points) {
    const n = points.length;
    if (n <= 1) return Infinity;
    if (n === 2) return distance(points[0], points[1]);
    
    // 按x坐标排序
    points.sort((a, b) => a[0] - b[0]);
    
    return closestPairRecursive(points, 0, n - 1);
}

function closestPairRecursive(points, left, right) {
    if (right - left <= 2) {
        return bruteForce(points, left, right);
    }
    
    const mid = Math.floor((left + right) / 2);
    const midX = points[mid][0];
    
    const dLeft = closestPairRecursive(points, left, mid);
    const dRight = closestPairRecursive(points, mid + 1, right);
    let d = Math.min(dLeft, dRight);
    
    // 检查跨越分割线的点
    const strip = [];
    for (let i = left; i <= right; i++) {
        if (Math.abs(points[i][0] - midX) < d) {
            strip.push(points[i]);
        }
    }
    
    strip.sort((a, b) => a[1] - b[1]);
    for (let i = 0; i < strip.length; i++) {
        for (let j = i + 1; j < strip.length && strip[j][1] - strip[i][1] < d; j++) {
            d = Math.min(d, distance(strip[i], strip[j]));
        }
    }
    
    return d;
}

function distance(p1, p2) {
    return Math.sqrt((p1[0] - p2[0]) ** 2 + (p1[1] - p2[1]) ** 2);
}`,
      python: `# 最近点对算法
import math

def closest_pair(points):
    points.sort(key=lambda p: p[0])
    return closest_recursive(points, 0, len(points) - 1)

def closest_recursive(points, left, right):
    if right - left <= 2:
        return brute_force(points, left, right)
    
    mid = (left + right) // 2
    mid_x = points[mid][0]
    
    d_left = closest_recursive(points, left, mid)
    d_right = closest_recursive(points, mid + 1, right)
    d = min(d_left, d_right)
    
    # 检查跨越分割线的点
    strip = [p for p in points[left:right+1] if abs(p[0] - mid_x) < d]
    strip.sort(key=lambda p: p[1])
    
    for i in range(len(strip)):
        j = i + 1
        while j < len(strip) and strip[j][1] - strip[i][1] < d:
            d = min(d, distance(strip[i], strip[j]))
            j += 1
    
    return d

def distance(p1, p2):
    return math.sqrt((p1[0] - p2[0]) ** 2 + (p1[1] - p2[1]) ** 2)`,
      cpp: `// 最近点对算法
double distance(pair<double,double>& p1, pair<double,double>& p2) {
    return sqrt(pow(p1.first - p2.first, 2) + pow(p1.second - p2.second, 2));
}

double closestPair(vector<pair<double,double>>& points) {
    sort(points.begin(), points.end());
    return closestRecursive(points, 0, points.size() - 1);
}

double closestRecursive(vector<pair<double,double>>& points, int left, int right) {
    if (right - left <= 2) {
        double minDist = DBL_MAX;
        for (int i = left; i <= right; i++) {
            for (int j = i + 1; j <= right; j++) {
                minDist = min(minDist, distance(points[i], points[j]));
            }
        }
        return minDist;
    }
    
    int mid = (left + right) / 2;
    double midX = points[mid].first;
    
    double d = min(closestRecursive(points, left, mid),
                   closestRecursive(points, mid + 1, right));
    
    vector<pair<double,double>> strip;
    for (int i = left; i <= right; i++) {
        if (abs(points[i].first - midX) < d) {
            strip.push_back(points[i]);
        }
    }
    
    sort(strip.begin(), strip.end(), 
         [](auto& a, auto& b) { return a.second < b.second; });
    
    for (int i = 0; i < strip.size(); i++) {
        for (int j = i + 1; j < strip.size() && strip[j].second - strip[i].second < d; j++) {
            d = min(d, distance(strip[i], strip[j]));
        }
    }
    return d;
}`
    }
  }
};

/**
 * 获取算法配置
 */
export function getAlgorithm(id: string): AlgorithmConfig | undefined {
  return algorithms[id];
}

/**
 * 获取分类下的所有算法
 */
export function getAlgorithmsByCategory(categoryId: string): AlgorithmConfig[] {
  const category = algorithmCategories.find(c => c.id === categoryId);
  if (!category) return [];
  return category.algorithms.map(id => algorithms[id]).filter(Boolean);
}

/**
 * 格式化代码（添加语法高亮）
 */
export function formatCodeWithHighlight(code: string): string {
  return code
    .replace(/\/\/.*/g, '<span class="code-comment">$&</span>')
    .replace(/\b(function|const|let|var|if|else|for|while|return|new|class|constructor|this)\b/g, '<span class="code-keyword">$1</span>')
    .replace(/\b(\d+)\b/g, '<span class="code-number">$1</span>')
    .replace(/([a-zA-Z_]\w*)\s*\(/g, '<span class="code-function">$1</span>(');
}
